/**
 * What the clock of a simulation run says: the one rule a chart's `patient_time` and the Medical Office calendar's
 * "today" both follow, so a calendar and a chart in one run never show different times.
 *
 * The run keeps its own offset (`simulation_clock_offset`, minutes, moved by a time warp and by a resume after a
 * pause). While the run is running the time is the real time plus that offset; while it is paused or completed it
 * is the instant it was paused at plus the offset, so it does not move. Mirrors `Patient.get_patient_time` on the
 * backend.
 *
 * The caller has already decided there is a clock to read (`run.simulation_clock_offset !== null`); this is only
 * the arithmetic. `now` is a parameter so a test can hold time still.
 */
export function simulationNow (run, now) {
  // Elapsed time, as the backend adds it. `date.addToDate` adds wall-clock time, which is an hour out when a
  // daylight-saving change falls inside the offset.
  const ms = run.simulation_clock_offset * 60 * 1000
  if (run.status === 'running' && run.completed !== true) {
    return new Date(new Date(now).getTime() + ms)
  }
  return new Date(new Date(run.paused_datetime).getTime() + ms)
}

/**
 * The time a Medical Office calendar treats as now: the run's clock when the activity has a run with one, else the
 * real time. Unlike a chart, the calendar can be open when the run has no clock yet (before it starts) or a
 * malformed one, and must still have a "today".
 */
export const hasClock = (run) => !!run && run.simulation_clock_offset !== null && run.simulation_clock_offset !== undefined

/**
 * The clock to hand a calendar or a booking dialog as `now`: the run's, when the run has one; otherwise null, so the
 * time model reads its own default (the real time, and for "has this passed" the patient clock). One rule for every
 * caller, so a simulation that has no clock yet is not judged on the wall clock alone.
 */
export const clockOf = (run) => (hasClock(run) ? () => calendarNow(run) : null)

/** Whether the run's clock is moving (running, not completed): a paused or completed run's time is fixed. */
export const runIsMoving = (run) => !!run && run.status === 'running' && run.completed !== true

export function calendarNow (run, now = new Date()) {
  if (!run || run.simulation_clock_offset === null || run.simulation_clock_offset === undefined) return now
  const simulated = simulationNow(run, now)
  return Number.isNaN(simulated.getTime()) ? now : simulated
}
